import React, { useState, useEffect } from 'react';
import { Box, Typography, useMediaQuery, Skeleton } from '@mui/material';
import { getSteamChartData } from '../../services/api';

interface SteamChartData {
  timestamp: number;
  players: number;
}

interface SteamActiveUsersGraphProps {
  steamId: string;
  onCurrentPlayersChange?: (players: number) => void;
}

const formatNumber = (num: number): string => {
  return num.toLocaleString();
};

const formatDate = (timestamp: number): string => {
  const date = new Date(timestamp);
  return `${date.getMonth() + 1}/${date.getDate()}`;
};

const formatDateTime = (timestamp: number): string => {
  const date = new Date(timestamp);
  return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:${date.getMinutes().toString().padStart(2, '0')}`;
};

const SteamActiveUsersGraph: React.FC<SteamActiveUsersGraphProps> = ({ steamId, onCurrentPlayersChange }) => {
  const [chartData, setChartData] = useState<SteamChartData[]>([]);
  const [dayData, setDayData] = useState<SteamChartData[]>([]);
  const [monthData, setMonthData] = useState<SteamChartData[]>([]);
  const [allTimeData, setAllTimeData] = useState<SteamChartData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoveredPoint, setHoveredPoint] = useState<{ x: number; y: number; data: SteamChartData } | null>(null);
  const [hoveredAllTimePoint, setHoveredAllTimePoint] = useState<{ x: number; y: number; data: SteamChartData } | null>(null);
  const isMobile = useMediaQuery('(max-width:600px)');
  
  // polylineの全長を計算用（条件付きレンダリング前に宣言）
  const polylineRef = React.useRef<SVGPolylineElement>(null);
  const [pathLength, setPathLength] = React.useState(0);

  useEffect(() => {
    const fetchSteamData = async () => {
      try {
        setLoading(true);
        const rawData = await getSteamChartData(steamId);
        
                 // 各期間のデータを効率的に取得
         const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
         const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
         const oneMonthAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
         
         const dayData: SteamChartData[] = [];
         const weekData: SteamChartData[] = [];
         const monthDataTemp: SteamChartData[] = [];
         const allTimeDataTemp: SteamChartData[] = [];
         
         // 全データを一度だけ処理して各期間に分類
         for (let i = rawData.length - 1; i >= 0; i--) {
           const item = rawData[i];
           const dataPoint = {
             timestamp: item[0],
             players: item[1]
           };
           
           // 全期間データ
           allTimeDataTemp.unshift(dataPoint);
           
           // 1ヶ月以内のデータ
           if (item[0] >= oneMonthAgo) {
             monthDataTemp.unshift(dataPoint);
           }
           
           // 1週間以内のデータ
           if (item[0] >= oneWeekAgo) {
             weekData.unshift(dataPoint);
           }
           
           // 24時間以内のデータ
           if (item[0] >= oneDayAgo) {
             dayData.unshift(dataPoint);
           }
         }

        setChartData(weekData);
        setDayData(dayData);
        setMonthData(monthDataTemp);
        setAllTimeData(allTimeDataTemp);
        setError(null);
        
        // 現在のプレイヤー数を親コンポーネントに通知
        if (onCurrentPlayersChange && monthDataTemp.length > 0) {
          const currentPlayers = monthDataTemp[monthDataTemp.length - 1].players;
          onCurrentPlayersChange(currentPlayers);
        }
      } catch (err) {
        console.error('Steam data fetch error:', err);
        setError('データの取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    if (steamId) {
      fetchSteamData();
    }
  }, [steamId]);

  // pathLengthを更新するuseEffect
  React.useEffect(() => {
    if (polylineRef.current) {
      const length = polylineRef.current.getTotalLength();
      setPathLength(length);
    }
  }, [chartData]); // chartDataが変更されたときに実行

  if (loading) {
    return (
      <Box sx={{ mt: { xs: 3, sm: 3 }, mb: 3 }}>
        <Typography variant="h6" component="h2" sx={{ mb: 2, fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
          Steam アクティブユーザー数
        </Typography>
        <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  if (error || chartData.length === 0) {
    return null; // エラー時やデータがない場合は非表示
  }

  // グラフの設定
  const width = 150;
  const height = 30;
  const padding = 3;
  const gridLines = 4;

  const maxPlayers = Math.max(...chartData.map(d => d.players));
  const minPlayers = Math.min(...chartData.map(d => d.players));
  const playerRange = maxPlayers - minPlayers;
  
  // Y軸の範囲を計算（最小値を底上げして見やすくする）
  const yMin = Math.max(0, minPlayers - playerRange * 0.1);
  const yMax = maxPlayers + playerRange * 0.1;
  const yRange = yMax - yMin;

  // グラフのポイントを計算
  const points = chartData.map((data, index) => {
    const x = (index / Math.max(chartData.length - 1, 1)) * (width - padding * 2) + padding;
    const y = height - ((data.players - yMin) / yRange) * (height - padding * 2) - padding;
    return `${x},${y}`;
  });

  // 水平グリッドラインと目盛りの値を計算
  const horizontalGridLines = Array.from({ length: gridLines }, (_, i) => {
    const y = padding + (i * ((height - padding * 2) / (gridLines - 1)));
    const value = Math.round(yMax - (i / (gridLines - 1)) * yRange);
    return { y, value };
  });

  // 月曜日のデータポイントを見つける（週の区切り用）
  const mondayPoints = chartData
    .map((data, index) => ({ data, index }))
    .filter(({ data }) => new Date(data.timestamp).getDay() === 1)
    .slice(0, 4); // 最大4つまで表示

  // 各期間のピーク値を計算
  const currentPlayers = chartData[chartData.length - 1]?.players || 0;
  const dayPeakPlayers = dayData.length > 0 ? Math.max(...dayData.map(d => d.players)) : 0;
  const monthPeakPlayers = monthData.length > 0 ? Math.max(...monthData.map(d => d.players)) : 0;
  const allTimePeakPlayers = allTimeData.length > 0 ? Math.max(...allTimeData.map(d => d.players)) : 0;

  return (
    <Box sx={{ mt: { xs: 3, sm: 3 }, mb: 3 }}>
        <Typography variant="h6" component="h2" sx={{ mb: 1.5, fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
          アクティブユーザー / Steam 
        </Typography>
      
      <Box sx={{ 
        display: 'flex', 
        gap: { xs: 2, sm: 3 }, 
        mb: 1.5,
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' }
      }}>
        {/* モバイル表示: 1つのボックスに全て */}
        <Box sx={{ 
          display: { xs: 'block', sm: 'none' },
          width: '100%',
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          padding: '8px 12px',
          borderRadius: '4px'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.6rem', whiteSpace: 'nowrap' }}>
                現在
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                {formatNumber(currentPlayers)}人
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.6rem', whiteSpace: 'nowrap' }}>
                24hピーク
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                {formatNumber(dayPeakPlayers)}人
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.6rem', whiteSpace: 'nowrap' }}>
                1mピーク
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                {formatNumber(monthPeakPlayers)}人
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.6rem', whiteSpace: 'nowrap' }}>
                最大ピーク
              </Typography>
              <Typography variant="body2" sx={{ color: '#d9ff00', fontWeight: 600, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                {formatNumber(allTimePeakPlayers)}人
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* デスクトップ表示: 個別ボックス */}
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1.5, flexWrap: 'wrap' }}>
          <Box sx={{ 
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            padding: '8px 16px',
            borderRadius: '4px',
            whiteSpace: 'nowrap'
          }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
              現在
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600, fontSize: '0.9rem', whiteSpace: 'nowrap' }}>
              {formatNumber(currentPlayers)}人
            </Typography>
          </Box>
          <Box sx={{ 
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            padding: '8px 16px',
            borderRadius: '4px',
            whiteSpace: 'nowrap'
          }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
              24時間ピーク
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600, fontSize: '0.9rem', whiteSpace: 'nowrap' }}>
              {formatNumber(dayPeakPlayers)}人
            </Typography>
          </Box>
          <Box sx={{ 
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            padding: '8px 16px',
            borderRadius: '4px',
            whiteSpace: 'nowrap'
          }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
              1ヶ月ピーク
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600, fontSize: '0.9rem', whiteSpace: 'nowrap' }}>
              {formatNumber(monthPeakPlayers)}人
            </Typography>
          </Box>
          <Box sx={{ 
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            padding: '8px 16px',
            borderRadius: '4px',
            whiteSpace: 'nowrap'
          }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
              最大ピーク
            </Typography>
            <Typography variant="body1" sx={{ color: '#d9ff00', fontWeight: 600, fontSize: '0.9rem', whiteSpace: 'nowrap' }}>
              {formatNumber(allTimePeakPlayers)}人
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* メインの1週間グラフ */}
      <Box sx={{ 
        position: 'relative',
        mb: 1
      }}>
        <svg
          viewBox={`0 0 ${width} ${height}`}
          style={{ 
            width: '100%',
            height: 'auto',
            aspectRatio: `${width} / ${height}`,
            maxHeight: '200px'
          }}
          onMouseLeave={() => setHoveredPoint(null)}
        >
          {/* グラデーションの定義 */}
          <defs>
            <linearGradient id="steamGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(217, 255, 0, 0)">
                <animate
                  attributeName="offset"
                  values="-1; 2"
                  dur="4s"
                  repeatCount="indefinite"
                />
              </stop>
              <stop offset="50%" stopColor="rgba(217, 255, 0, 0.06)">
                <animate
                  attributeName="offset"
                  values="-0.5; 2.5"
                  dur="4s"
                  repeatCount="indefinite"
                />
              </stop>
              <stop offset="100%" stopColor="rgba(217, 255, 0, 0)">
                <animate
                  attributeName="offset"
                  values="0; 3"
                  dur="4s"
                  repeatCount="indefinite"
                />
              </stop>
            </linearGradient>

            <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(217, 255, 0, 0.3)" />
              <stop offset="100%" stopColor="rgba(217, 255, 0, 0.02)" />
            </linearGradient>
          </defs>

          {/* アニメーションする背景 */}
          <rect
            x={padding}
            y={padding}
            width={width - padding * 2}
            height={height - padding * 2}
            fill="url(#steamGradient)"
          />

          {/* 背景のベースライン */}
          <rect
            x={padding}
            y={padding}
            width={width - padding * 2}
            height={height - padding * 2}
            fill="none"
            stroke="rgba(255, 255, 255, 0.1)"
            strokeWidth="0.2"
          />

                     {/* 水平グリッドライン */}
           {horizontalGridLines.map(({ y }, i) => (
             <line
               key={`h-${i}`}
               x1={padding}
               y1={y}
               x2={width - padding}
               y2={y}
               stroke="rgba(255, 255, 255, 0.1)"
               strokeWidth="0.2"
               strokeDasharray={i === gridLines - 1 ? "none" : "0.5,0.5"}
             />
           ))}

                     {/* 1日ごとの垂直グリッドラインとラベル */}
           {chartData.map((data, i) => {
             // 1日ごとに表示（データ密度に応じて間引き）
             const shouldShow = i % Math.max(1, Math.floor(chartData.length / 7)) === 0 || i === chartData.length - 1;
             if (!shouldShow) return null;
             
             const x = (i / Math.max(chartData.length - 1, 1)) * (width - padding * 2) + padding;
             return (
               <React.Fragment key={`v-${i}`}>
                 <line
                   x1={x}
                   y1={padding}
                   x2={x}
                   y2={height - padding}
                   stroke="rgba(255, 255, 255, 0.1)"
                   strokeWidth="0.2"
                   strokeDasharray="0.5,0.5"
                 />
                 <text
                   x={x}
                   y={height - 0.5}
                   fill="rgba(255, 255, 255, 0.5)"
                   fontSize="2.5"
                   textAnchor="middle"
                 >
                   {formatDate(data.timestamp)}
                 </text>
               </React.Fragment>
             );
           })}

          {/* エリアチャート（塗りつぶし） */}
          {points.length > 0 && (
            <polygon
              points={`${padding},${height - padding} ${points.join(' ')} ${width - padding},${height - padding}`}
              fill="url(#areaGradient)"
              style={{
                opacity: 0,
                animation: 'fadeIn 1s ease-out forwards 0.5s'
              }}
            />
          )}

          {/* プレイヤー数の折れ線（スムーズカーブ） */}
          <path
            ref={polylineRef}
            fill="none"
            stroke="#d9ff00"
            strokeWidth="0.3"
            strokeOpacity="0.8"
            d={(() => {
              if (points.length < 2) return '';
              
              const pointsArray = points.map(p => p.split(',').map(Number));
              let path = `M ${pointsArray[0][0]},${pointsArray[0][1]}`;
              
              for (let i = 1; i < pointsArray.length; i++) {
                const [currX, currY] = pointsArray[i];
                
                if (i === 1) {
                  // 最初のカーブ
                  const [prevX, prevY] = pointsArray[i - 1];
                  const cp1x = prevX + (currX - prevX) * 0.3;
                  const cp1y = prevY;
                  const cp2x = currX - (currX - prevX) * 0.3;
                  const cp2y = currY;
                  path += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${currX},${currY}`;
                } else {
                  // 滑らかな継続カーブ
                  path += ` S ${currX - (currX - pointsArray[i-1][0]) * 0.2},${currY} ${currX},${currY}`;
                }
              }
              
              return path;
            })()}
            style={{
              strokeDasharray: pathLength,
              strokeDashoffset: pathLength,
              animation: `dash 2s ease-in-out forwards`,
              opacity: 0,
              animationFillMode: 'forwards'
            }}
          />

          {/* ホバーエリア（縦軸全体） */}
          {points.map((point, i) => {
            const [x] = point.split(',').map(Number);
            return (
              <rect
                key={i}
                x={x - 1}
                y={padding}
                width="2"
                height={height - padding * 2}
                fill="transparent"
                style={{
                  cursor: 'pointer'
                }}
                onMouseEnter={() => {
                  const [, y] = point.split(',').map(Number);
                  setHoveredPoint({ x, y, data: chartData[i] });
                }}
              />
            );
          })}

          {/* ホバー時の垂直線 */}
          {hoveredPoint && (
            <line
              x1={hoveredPoint.x}
              y1={padding}
              x2={hoveredPoint.x}
              y2={height - padding}
              stroke="rgba(217, 255, 0, 0.5)"
              strokeWidth="0.2"
              strokeDasharray="1,1"
            />
          )}

          <style>
            {`
              @keyframes dash {
                0% {
                  opacity: 0;
                  stroke-dashoffset: ${pathLength};
                }
                10% {
                  opacity: 1;
                }
                100% {
                  opacity: 1;
                  stroke-dashoffset: 0;
                }
              }
              @keyframes fadeIn {
                from {
                  opacity: 0;
                }
                to {
                  opacity: 1;
                }
              }
            `}
          </style>
        </svg>

        {/* ツールチップ */}
        {hoveredPoint && (
          <Box
            sx={{
              position: 'absolute',
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              color: 'white',
              padding: '8px 12px',
              borderRadius: '6px',
              fontSize: '0.75rem',
              fontWeight: 500,
              pointerEvents: 'none',
              zIndex: 10,
              border: '1px solid rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(8px)',
              left: `${(hoveredPoint.x / width) * 100}%`,
              bottom: `${100 - (hoveredPoint.y / height) * 100 + 10}%`,
              transform: 'translateX(-50%)',
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
            }}
          >
            <Box sx={{ mb: 0.5, color: '#d9ff00', fontWeight: 600 }}>
              {formatNumber(hoveredPoint.data.players)}人
            </Box>
            <Box sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.7rem' }}>
              {formatDateTime(hoveredPoint.data.timestamp)}
            </Box>
          </Box>
        )}
      </Box>

      {/* 全期間の小さなグラフ */}
      {allTimeData.length > 0 && (
        <Box sx={{ 
          position: 'relative'
        }}>
          <Typography variant="body2" sx={{ mb: 0, fontSize: { xs: '0.6rem', sm: '0.6rem' }, color: 'rgba(255, 255, 255, 0.8)' }}>
            全期間の推移
          </Typography>
                      {(() => {
              // 全期間グラフの設定
              const allTimeWidth = 150;
              const allTimeHeight = 15;
              const allTimePadding = 2;
            
            const allTimeMaxPlayers = Math.max(...allTimeData.map(d => d.players));
            const allTimeMinPlayers = Math.min(...allTimeData.map(d => d.players));
            const allTimePlayerRange = allTimeMaxPlayers - allTimeMinPlayers;
            
            // 最小の高さを保証（最大値の30%以上の範囲を確保）
            const minRangeRatio = 0.3;
            const minRange = allTimeMaxPlayers * minRangeRatio;
            const adjustedRange = Math.max(allTimePlayerRange, minRange);
            
            // 調整された範囲を使用してY軸の範囲を計算
            const rangeDiff = adjustedRange - allTimePlayerRange;
            const allTimeYMin = Math.max(0, allTimeMinPlayers - adjustedRange * 0.1 - rangeDiff / 2);
            const allTimeYMax = allTimeMaxPlayers + adjustedRange * 0.1 + rangeDiff / 2;
            const allTimeYRange = allTimeYMax - allTimeYMin;

                                     // 適応的サンプリング：3ヶ月以内は週単位、それ以前は月単位
            const now = Date.now();
            const threeMonthsAgo = now - 3 * 30 * 24 * 60 * 60 * 1000;
            
            const weeklyData: { [key: string]: SteamChartData } = {};
            const monthlyData: { [key: string]: SteamChartData } = {};
            
            for (const data of allTimeData) {
              const date = new Date(data.timestamp);
              const year = date.getFullYear();
              const month = date.getMonth() + 1;
              const day = date.getDate();
              
              if (data.timestamp >= threeMonthsAgo) {
                // 3ヶ月以内：週単位でサンプリング（月曜日基準）
                const dayOfWeek = date.getDay(); // 0=日曜, 1=月曜
                const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
                const mondayDate = new Date(date);
                mondayDate.setDate(date.getDate() - daysToMonday);
                
                const weekKey = `${mondayDate.getFullYear()}-${(mondayDate.getMonth() + 1).toString().padStart(2, '0')}-${mondayDate.getDate().toString().padStart(2, '0')}`;
                
                if (!weeklyData[weekKey] || data.players > weeklyData[weekKey].players) {
                  weeklyData[weekKey] = data;
                }
              } else {
                // 3ヶ月以前：月単位でサンプリング（1日基準）
                if (day === 1) {
                  const monthKey = `${year}-${month.toString().padStart(2, '0')}`;
                  
                  if (!monthlyData[monthKey] || data.players > monthlyData[monthKey].players) {
                    monthlyData[monthKey] = data;
                  }
                }
              }
            }
            
            // 両方のデータをマージして時系列順にソート
            const sampledData = [
              ...Object.values(monthlyData),
              ...Object.values(weeklyData)
            ].sort((a, b) => a.timestamp - b.timestamp);
            
            // 最新データを必ず含める
            if (sampledData.length > 0 && allTimeData.length > 0) {
              const lastSampledTime = sampledData[sampledData.length - 1].timestamp;
              const latestTime = allTimeData[allTimeData.length - 1].timestamp;
              
              // 最新データが7日以上新しい場合は追加
              if (latestTime - lastSampledTime > 7 * 24 * 60 * 60 * 1000) {
                sampledData.push(allTimeData[allTimeData.length - 1]);
              }
            }

            // 時間軸に基づいた正確な横軸位置の計算
            const allTimePoints = sampledData.map((data) => {
              const minTime = sampledData[0]?.timestamp || 0;
              const maxTime = sampledData[sampledData.length - 1]?.timestamp || 0;
              const timeRange = maxTime - minTime;
              
              // 実際の時間差に基づいて横軸位置を計算
              const timeRatio = timeRange > 0 ? (data.timestamp - minTime) / timeRange : 0;
              const x = timeRatio * (allTimeWidth - allTimePadding * 2) + allTimePadding;
              const y = allTimeHeight - ((data.players - allTimeYMin) / allTimeYRange) * (allTimeHeight - allTimePadding * 2) - allTimePadding;
              return `${x},${y}`;
            });

                         return (
               <svg
                 viewBox={`0 0 ${allTimeWidth} ${allTimeHeight}`}
                 style={{ 
                   width: '100%',
                   height: 'auto',
                   aspectRatio: `${allTimeWidth} / ${allTimeHeight}`,
                   maxHeight: '120px'
                 }}
                 onMouseLeave={() => setHoveredAllTimePoint(null)}
               >
                {/* エリアチャート */}
                {allTimePoints.length > 0 && (
                  <polygon
                    points={`${allTimePadding},${allTimeHeight - allTimePadding} ${allTimePoints.join(' ')} ${allTimeWidth - allTimePadding},${allTimeHeight - allTimePadding}`}
                    fill="rgba(217, 255, 0, 0.1)"
                  />
                )}

                                 {/* 折れ線（スムーズカーブ） */}
                 <path
                   fill="none"
                   stroke="#d9ff00"
                   strokeWidth="0.3"
                   strokeOpacity="0.6"
                   d={(() => {
                     if (allTimePoints.length < 2) return '';
                     
                     const pointsArray = allTimePoints.map(p => p.split(',').map(Number));
                     
                     // データポイント間の距離が極端に離れている場合は直線で接続
                     const avgDistance = pointsArray.length > 1 ? 
                       pointsArray.reduce((sum, point, i) => {
                         if (i === 0) return 0;
                         return sum + Math.abs(point[0] - pointsArray[i-1][0]);
                       }, 0) / (pointsArray.length - 1) : 0;
                     
                     let path = `M ${pointsArray[0][0]},${pointsArray[0][1]}`;
                     
                     for (let i = 1; i < pointsArray.length; i++) {
                       const [currX, currY] = pointsArray[i];
                       const [prevX, prevY] = pointsArray[i - 1];
                       
                       const distance = Math.abs(currX - prevX);
                       const isLargeGap = distance > avgDistance * 2;
                       
                       if (isLargeGap || pointsArray.length < 4) {
                         // 大きなギャップまたは少ないポイント数の場合は直線
                         path += ` L ${currX},${currY}`;
                       } else {
                         // 適度な距離の場合のみ曲線を使用
                         if (i === 1) {
                           const cp1x = prevX + (currX - prevX) * 0.2;
                           const cp1y = prevY;
                           const cp2x = currX - (currX - prevX) * 0.2;
                           const cp2y = currY;
                           path += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${currX},${currY}`;
                         } else {
                           path += ` S ${currX - (currX - prevX) * 0.1},${currY} ${currX},${currY}`;
                         }
                       }
                     }
                     
                     return path;
                   })()}
                 />

                 {/* ホバーエリア（全期間グラフ） */}
                 {allTimePoints.map((point, i) => {
                   const [x] = point.split(',').map(Number);
                   return (
                     <rect
                       key={`all-time-hover-${i}`}
                       x={x - 1}
                       y={allTimePadding}
                       width="2"
                       height={allTimeHeight - allTimePadding * 2}
                       fill="transparent"
                       style={{
                         cursor: 'pointer'
                       }}
                       onMouseEnter={() => {
                         const [, y] = point.split(',').map(Number);
                         setHoveredAllTimePoint({ x, y, data: sampledData[i] });
                       }}
                     />
                   );
                 })}

                 {/* ホバー時の垂直線（全期間） */}
                 {hoveredAllTimePoint && (
                   <line
                     x1={hoveredAllTimePoint.x}
                     y1={allTimePadding}
                     x2={hoveredAllTimePoint.x}
                     y2={allTimeHeight - allTimePadding}
                     stroke="rgba(217, 255, 0, 0.5)"
                     strokeWidth="0.2"
                     strokeDasharray="1,1"
                   />
                 )}

                 {/* 5分割の年月ラベル（時間軸基準） */}
                 {Array.from({ length: 5 }, (_, i) => {
                   if (sampledData.length === 0) return null;
                   
                   const minTime = sampledData[0].timestamp;
                   const maxTime = sampledData[sampledData.length - 1].timestamp;
                   const timeRange = maxTime - minTime;
                   
                   // 時間軸上での等分位置を計算
                   const targetTime = minTime + (timeRange * i / 4);
                   
                   // 最も近いデータポイントを見つける
                   let closestData = sampledData[0];
                   let minDiff = Math.abs(sampledData[0].timestamp - targetTime);
                   
                   for (const data of sampledData) {
                     const diff = Math.abs(data.timestamp - targetTime);
                     if (diff < minDiff) {
                       minDiff = diff;
                       closestData = data;
                     }
                   }
                   
                   // 実際の時間差に基づいて横軸位置を計算
                   const timeRatio = timeRange > 0 ? (closestData.timestamp - minTime) / timeRange : 0;
                   const x = timeRatio * (allTimeWidth - allTimePadding * 2) + allTimePadding;
                   const date = new Date(closestData.timestamp);
                   const year = date.getFullYear().toString().slice(-2); // 下2桁
                   const month = (date.getMonth() + 1).toString().padStart(2, '0');
                   const yearMonth = `${year}.${month}`;
                   
                   return (
                     <React.Fragment key={`year-month-${i}`}>
                       <line
                         x1={x}
                         y1={allTimePadding}
                         x2={x}
                         y2={allTimeHeight - allTimePadding}
                         stroke="rgba(255, 255, 255, 0.1)"
                         strokeWidth="0.1"
                         strokeDasharray="0.3,0.3"
                       />
                       <text
                         x={x}
                         y={allTimeHeight - 0.3}
                         fill="rgba(255, 255, 255, 0.5)"
                         fontSize="2.5"
                         textAnchor="middle"
                       >
                         {yearMonth}
                       </text>
                     </React.Fragment>
                   );
                 })}

                                
              </svg>
            );
          })()}

          {/* 全期間グラフのツールチップ */}
          {hoveredAllTimePoint && (
            <Box
              sx={{
                position: 'absolute',
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                color: 'white',
                padding: '8px 12px',
                borderRadius: '6px',
                fontSize: '0.75rem',
                fontWeight: 500,
                pointerEvents: 'none',
                zIndex: 10,
                border: '1px solid rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(8px)',
                left: `${(hoveredAllTimePoint.x / 150) * 100}%`,
                bottom: `${100 - (hoveredAllTimePoint.y / 25) * 100 + 10}%`,
                transform: 'translateX(-50%)',
                whiteSpace: 'nowrap',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
              }}
            >
              <Box sx={{ mb: 0.5, color: '#d9ff00', fontWeight: 600 }}>
                {formatNumber(hoveredAllTimePoint.data.players)}人
              </Box>
              <Box sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.7rem' }}>
                {formatDateTime(hoveredAllTimePoint.data.timestamp)}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default SteamActiveUsersGraph;
